<template>
  <div>
    <v-autocomplete
      :value="selectedParticipants"
      @input="addParticipant"
      :items="queriedUsers"
      :loading="isLoading"
      filled
      chips
      color="rgb(255, 149, 24)"
      label="Start typing to search users"
      item-text="name"
      item-value="uuid"
      multiple
      item-color="orange"
      hide-details
      hide-no-data
      hide-selected
      prepend-inner-icon="mdi-account-search"
      :search-input.sync="search"
      return-object
      disable-lookup
      no-filter
      menu-props="{ bottom: true }"
      cache-items
      ref="autocomplete"
    >
      <template v-slot:selection="data">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-chip
              v-if="!(data.item.uuid === '' && data.item.name === 'Invite New User')"
              v-bind="data.attrs"
              :input-value="data.selected"
              close
              @click="data.select"
              @click:close="removeParticipant(data.item.uuid)"
              v-on="on"
            >
              <v-avatar :color="data.item.avatar.color" left>
                <span class="white--text">{{ data.item.avatar.initials }}</span>
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <span>{{ data.item.email }}</span>
        </v-tooltip>
      </template>
      <template
        v-slot:item="data"
      >
        <v-list-item-icon
          v-if="data.item.uuid === '' && data.item.name === 'Invite New User'"
        >
          <v-icon>mdi-account-plus</v-icon>
        </v-list-item-icon>
        <v-list-item-avatar
          v-else
          :color="data.item.avatar.color"
        >
          <span class="white--text headline">{{ data.item.avatar.initials }}</span>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title v-html="data.item.name"/>
          <v-list-item-subtitle v-html="data.item.email" />
        </v-list-item-content>
      </template>
    </v-autocomplete>
    <InviteUserModal
      :isShown.sync="addUserDialog"
      @add-user="inviteNewUser"
    />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import InviteUserModal from './InviteUserModal'

export default {
  name: 'SelectParticipants',

  components: {
    InviteUserModal
  },

  props: ['type', 'organizationUUID'],

  data: () => ({
    search: null,
    isLoading: false,
    // users found for all past queries for the current session
    // cachedQueriedUsers: [],
    // users found for the current query
    queriedUsers: [],

    // the reason we have 2 separate arrays is because upon a query,
    // the queriedUsers array changes. We need a master list of users
    // that stays constant and provides all the information for the users
    // currentlyViewedChallenge.participants only offers the challenge IDs
    // therefore, in selectedParticipants.vue, we query the backend for all
    // the user objects for the uuids

    addUserDialog: false,
    selectedParticipants: []
  }),

  async mounted () {
    this.setupParticipants()
  },

  computed: {
    ...mapState({
      currentlyViewedChallenge: state => state.challenge.currentlyViewedChallenge
    })
  },

  watch: {
    async search (query) {
      if (query === null || query === '') return
      // Items have already been requested
      if (this.isLoading) return

      this.isLoading = true

      const results = await this.requestSearchUserByNameOrEmail({
        query: query,
        organizationUUID: this.organizationUUID,
        type: this.type
      })

      if (results.users.length > 0) {
        // show all participants picked so far, as well as the user results
        // const entries = [...new Set([...results.users, ...this.cachedQueriedUsers])]

        this.queriedUsers = results.users

        if (this.type === 'facilitators') {
          this.queriedUsers = this.queriedUsers.filter(u => {
            return !this.currentlyViewedChallenge.participants.includes(u.uuid)
          })
        } else if (this.type === 'participants') {
          this.queriedUsers = this.queriedUsers.filter(u => {
            return !this.currentlyViewedChallenge.facilitators.includes(u.uuid)
          })
        }
      }
      // else {
      //   const entries = JSON.parse(JSON.stringify(this.cachedQueriedUsers))

      //   this.queriedUsers = entries
      // }

      // add new user button
      this.queriedUsers.unshift({
        uuid: '',
        name: 'Invite New User'
      })
      this.$refs.autocomplete.cachedItems = [...this.queriedUsers, ...this.selectedParticipants]
      this.isLoading = false
    },
    organizationUUID: {
      handler: 'setupParticipants'
    }
  },

  methods: {
    ...mapMutations([
      'setCurrentlyViewedChallenge'
    ]),
    ...mapActions([
      'authorizedFetch',
      'requestSearchUserByNameOrEmail',
      'requestSearchUserByUUID'
    ]),

    async setupParticipants () {
      this.queriedUsers = []
      this.selectedParticipants = []
      this.$refs.autocomplete.cachedItems = []
      for (var participantId of this.currentlyViewedChallenge[this.type]) {
        var user = await this.requestSearchUserByUUID(participantId)
        this.queriedUsers.push(user)
        this.selectedParticipants.push(user)
      }
    },

    addParticipant (participantsToAdd) {
      if (participantsToAdd.find(p => p.uuid === '')) {
        // remove from vuetify autocomplete cache so the button doesn't disappear
        const autocompleteLazyValueIndex = this.$refs.autocomplete.lazyValue.findIndex(p => {
          return p.uuid === ''
        })
        if (autocompleteLazyValueIndex >= 0) {
          this.$refs.autocomplete.lazyValue.splice(autocompleteLazyValueIndex, 1)
        }
        this.addUserDialog = true
      } else {
        const participantsWithoutNewUser = participantsToAdd.filter(p => p.uuid !== '')
        this.selectedParticipants = participantsWithoutNewUser
        const updatedChallenge = JSON.parse(JSON.stringify(this.currentlyViewedChallenge))
        updatedChallenge[this.type] = participantsWithoutNewUser.map(p => p.uuid)
        this.setCurrentlyViewedChallenge(updatedChallenge)
      }
    },

    removeParticipant (partToRemoveId) {
      // update local selectedParticipants
      const selectedParticipantsIndex = this.selectedParticipants.findIndex(p => {
        return p.uuid === partToRemoveId
      })
      console.error(selectedParticipantsIndex)
      if (selectedParticipantsIndex >= 0) {
        this.selectedParticipants.splice(selectedParticipantsIndex, 1)
      }

      // update vuex
      const index = this.currentlyViewedChallenge[this.type].findIndex(p => {
        return p === partToRemoveId
      })
      if (index >= 0) {
        const updatedChallenge = JSON.parse(JSON.stringify(this.currentlyViewedChallenge))
        updatedChallenge[this.type].splice(index, 1)
        this.setCurrentlyViewedChallenge(updatedChallenge)
      }
    },

    inviteNewUser (email) {
      // const cVc = JSON.parse(JSON.stringify(this.currentlyViewedChallenge))
      // cVc[this.type].push(email)
      // this.setCurrentlyViewedChallenge(cVc)
      // console.error(this.currentlyViewedChallenge)
      const newUser = {
        avatar: {
          color: 'grey',
          initials: 'NU'
        },
        email: email,
        name: email,
        username: email,
        uuid: email
      }
      this.selectedParticipants.push(newUser)
      this.queriedUsers.push(newUser)

      const updatedChallenge = JSON.parse(JSON.stringify(this.currentlyViewedChallenge))
      updatedChallenge[this.type] = this.selectedParticipants.map(p => p.uuid)
      this.setCurrentlyViewedChallenge(updatedChallenge)
    }
  }
}
</script>
